import { Theme } from "@mui/material/styles/createTheme";
import { SxProps } from "@mui/system/styleFunctionSx";

export const sxProps: Record<string, SxProps<Theme>> = {
  container: {
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  paragraph: {
    marginBottom: (theme) => theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },

  bulletList: {
    marginTop: 0,
    marginBottom: (theme) => theme.spacing(1.5),
    paddingLeft: (theme) => theme.spacing(3),
    listStyleType: "disc",
    "& ul": {
      listStyleType: "circle",
      marginBottom: 0,
    },
    "& ul ul": {
      listStyleType: "square",
    },
  },

  orderedList: {
    marginTop: 0,
    marginBottom: (theme) => theme.spacing(1.5),
    paddingLeft: (theme) => theme.spacing(3),
    listStyleType: "decimal",
    "& ol": {
      listStyleType: "lower-alpha",
      marginBottom: 0,
    },
    "& ol ol": {
      listStyleType: "lower-roman",
    },
  },

  listItem: {
    marginBottom: (theme) => theme.spacing(0.5),
    "& > p": {
      marginBottom: 0,
    },
    "&:last-child": {
      marginBottom: 0,
    },
  },

  heading: {
    marginTop: (theme) => theme.spacing(2),
    marginBottom: (theme) => theme.spacing(1),
    "&:first-of-type": {
      marginTop: 0,
    },
  },

  blockquote: {
    margin: (theme) => theme.spacing(1.5, 0),
    padding: (theme) => theme.spacing(1, 2),
    borderLeft: (theme) => `4px solid ${theme.palette.grey[300]}`,
    backgroundColor: (theme) => theme.palette.grey[50],
    fontStyle: "italic",
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  codeBlock: {
    marginBottom: (theme) => theme.spacing(1.5),
    borderRadius: (theme) => theme.spacing(1),
    overflow: "hidden",
    backgroundColor: (theme) => theme.palette.grey[900],
  },

  codeLanguage: {
    display: "block",
    padding: (theme) => theme.spacing(0.5, 1.5),
    backgroundColor: (theme) => theme.palette.grey[800],
    color: (theme) => theme.palette.grey[400],
    fontSize: "0.75rem",
    textTransform: "uppercase",
  },

  pre: {
    margin: 0,
    padding: (theme) => theme.spacing(1.5),
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    color: (theme) => theme.palette.common.white,
    "& code": {
      fontFamily: "inherit",
    },
  },

  inlineCode: {
    padding: (theme) => theme.spacing(0.25, 0.5),
    borderRadius: (theme) => theme.spacing(0.5),
    backgroundColor: (theme) => theme.palette.grey[200],
    fontFamily: "monospace",
    fontSize: "0.875em",
  },

  link: {
    color: (theme) => theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "none",
    },
  },

  rule: {
    margin: (theme) => theme.spacing(2, 0),
    border: "none",
    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  },

  panel: {
    margin: (theme) => theme.spacing(1.5, 0),
    padding: (theme) => theme.spacing(1.5),
    borderRadius: (theme) => theme.spacing(1),
    borderLeft: "4px solid",
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  panel_info: {
    backgroundColor: (theme) => `${theme.palette.info.light}15`,
    borderLeftColor: (theme) => theme.palette.info.main,
  },

  panel_note: {
    backgroundColor: (theme) => `${theme.palette.primary.light}15`,
    borderLeftColor: (theme) => theme.palette.primary.main,
  },

  panel_warning: {
    backgroundColor: (theme) => `${theme.palette.warning.light}15`,
    borderLeftColor: (theme) => theme.palette.warning.main,
  },

  panel_success: {
    backgroundColor: (theme) => `${theme.palette.success.light}15`,
    borderLeftColor: (theme) => theme.palette.success.main,
  },

  panel_error: {
    backgroundColor: (theme) => `${theme.palette.error.light}15`,
    borderLeftColor: (theme) => theme.palette.error.main,
  },

  tableContainer: {
    marginBottom: (theme) => theme.spacing(1.5),
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: (theme) => `1px solid ${theme.palette.divider}`,
  },

  tableHeader: {
    padding: (theme) => theme.spacing(1),
    border: (theme) => `1px solid ${theme.palette.divider}`,
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: 600,
    textAlign: "left",
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  tableCell: {
    padding: (theme) => theme.spacing(1),
    border: (theme) => `1px solid ${theme.palette.divider}`,
    verticalAlign: "top",
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  mention: {
    padding: (theme) => theme.spacing(0.25, 0.5),
    borderRadius: (theme) => theme.spacing(0.5),
    backgroundColor: (theme) => `${theme.palette.primary.main}20`,
    color: (theme) => theme.palette.primary.main,
    fontWeight: 500,
  },

  date: {
    padding: (theme) => theme.spacing(0.25, 0.5),
    borderRadius: (theme) => theme.spacing(0.5),
    backgroundColor: (theme) => theme.palette.grey[200],
    fontSize: "0.875em",
  },

  status: {
    display: "inline-block",
    padding: (theme) => theme.spacing(0.25, 0.75),
    borderRadius: (theme) => theme.spacing(0.5),
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
  },

  status_neutral: {
    backgroundColor: (theme) => theme.palette.grey[200],
    color: (theme) => theme.palette.grey[700],
  },

  status_purple: {
    backgroundColor: "#EDE7F6",
    color: "#673AB7",
  },

  status_blue: {
    backgroundColor: (theme) => `${theme.palette.info.light}30`,
    color: (theme) => theme.palette.info.dark,
  },

  status_green: {
    backgroundColor: (theme) => `${theme.palette.success.light}30`,
    color: (theme) => theme.palette.success.dark,
  },

  status_yellow: {
    backgroundColor: (theme) => `${theme.palette.warning.light}30`,
    color: (theme) => theme.palette.warning.dark,
  },

  status_red: {
    backgroundColor: (theme) => `${theme.palette.error.light}30`,
    color: (theme) => theme.palette.error.dark,
  },

  inlineCard: {
    color: (theme) => theme.palette.primary.main,
    textDecoration: "underline",
    wordBreak: "break-all",
  },

  mediaPlaceholder: {
    margin: (theme) => theme.spacing(1.5, 0),
    padding: (theme) => theme.spacing(2),
    borderRadius: (theme) => theme.spacing(1),
    backgroundColor: (theme) => theme.palette.grey[100],
    textAlign: "center",
  },

  expand: {
    margin: (theme) => theme.spacing(1.5, 0),
    padding: (theme) => theme.spacing(1.5),
    borderRadius: (theme) => theme.spacing(1),
    border: (theme) => `1px solid ${theme.palette.divider}`,
    "& > *:last-child": {
      marginBottom: 0,
    },
  },

  expandTitle: {
    marginBottom: (theme) => theme.spacing(1),
    fontWeight: 600,
  },
};
